/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Component, OnInit, OnDestroy, AfterViewInit } from "@angular/core";
import { clientUtil, Logger } from "@html-core";
import { PrintFile, PrintRdService } from "./print-redirection.service";
import { NgbToastService } from "./../../common/service/ngb-toast.service";
import { Subscription } from "rxjs";
import { WmksService } from "../common/wmks.service";
import { TranslateService, EventBusService, BusEvent } from "@html-core";
import { printUtil } from "./print-util.service";
@Component({
   standalone: false,
   selector: "print-frame",
   templateUrl: "./print-frame.component.html"
})
export class PrintFrameComponent implements OnInit, OnDestroy, AfterViewInit {
   private printSubscription: Subscription;
   private isPrinting: boolean = false;
   private printFilesList: PrintFile[] = [];
   private frame: any = null;
   private logger: Logger = new Logger(Logger.PRINTER_FRAME);

   ngOnInit() {
      if (clientUtil.isFirefox() || clientUtil.isIE()) {
         return;
      }
      this.printSubscription = this.printRdService.printSubject.subscribe((msg) => {
         this.handlePrintMsg(msg);
      });
      this.eventBusService
         .listen(BusEvent.PrintFileUseOldSolutionMsg.MSG_TYPE)
         .subscribe((msg: BusEvent.PrintFileUseOldSolutionMsg) => {
            const data = msg.file;
            this.download(data);
         });
   }

   ngOnDestroy() {
      if (this.printSubscription && typeof this.printSubscription.unsubscribe === "function") {
         this.printSubscription.unsubscribe();
      }
   }

   ngAfterViewInit() {
      const self: PrintFrameComponent = this;
      self.frame = document.getElementById("printPDF");
      if (clientUtil.isFirefox() || clientUtil.isIE()) {
         return;
      } else {
         self.frame.onload = () => {
            this.logger.debug("Enter frame onload");
            if (!self.isPrinting) {
               this.logger.debug("No executing print job!");
               return;
            }
            let time = 0;
            if (clientUtil.isSafari()) {
               // Fix the safari blank preview issue
               time = 1000;
            }
            setTimeout(() => {
               self.frame.contentWindow.focus();
               this.logger.debug("Start to print");
               self.frame.contentWindow.print();
               $(self.frame.contentWindow).on("focus.print", () => {
                  $(self.frame.contentWindow).off("focus.print");
                  self.postPrint();
                  //get focus from iframe to window
                  window.focus();
               });
            }, time);
            /**
             * To fix bug 2681541, on Windows Chrome 87, can't get the
             * print dialog's focus leave event, so listen to document's click
             * event as a workaround, the limitation is that when print multi files
             * user need to click the desktop to print the next file.
             * */
            $(document).on("click", () => {
               $(document).off("click");
               self.postPrint();
            });
         };
      }
   }

   constructor(
      private wmksService: WmksService,
      private printRdService: PrintRdService,
      private toastService: NgbToastService,
      private translate: TranslateService,
      private eventBusService: EventBusService
   ) {}

   private handlePrintMsg = (msg) => {
      if (msg.type === PrintRdService.RECEIVED_PDF) {
         const pdf: PrintFile = msg.data;
         if (pdf.metaData && printUtil.isSupportChromeOrIWAPrinter()) {
            this.eventBusService.dispatch(new BusEvent.PrintFileMsg(pdf));
         } else {
            this.download(pdf);
         }
      } else if (msg.type == PrintRdService.RECEIVED_NOTIFICATION) {
         this.showToast(1);
      } else {
         this.logger.info("unknown message : " + msg.type);
      }
   };

   private showToast = (plus) => {
      const waitingLength = this.printFilesList.length + plus;
      this.toastService.clearAll();
      if (waitingLength > 0) {
         this.toastService.info(
            this.translate._T("PRINT_START") + "...(1/" + waitingLength + ")",
            this.toastService.TOAST_TYPE.PRINT
         );
      }
   };

   private download = (fileData) => {
      this.printFilesList.push(fileData);
      this.logger.info("File is printing: " + this.isPrinting);
      if (!this.isPrinting) {
         this.printFile();
      }
   };

   private printFile = () => {
      if (this.printFilesList.length > 0) {
         this.wmksService.shadowAllHeartbeatForPrint();
         const firstFile = this.printFilesList[0];

         if (this.frame) {
            this.isPrinting = true;
            const fileNew = new Blob([firstFile.pdfData], {
               type: "application/pdf"
            });
            this.frame.contentWindow.location.replace(window.URL.createObjectURL(fileNew));
         }
         //Delete the file once it is printed.
         this.removePrintDoneFile(firstFile.wmksKey, firstFile.jobId);
         this.printFilesList.shift();
      } else {
         this.logger.info("NO files need to be printed");
      }
   };

   private removePrintDoneFile = (key: string, printJodId: number) => {
      this.printRdService.removePrintDoneFile(key, printJodId);
   };

   private printNextFile = () => {
      this.showToast(0);
      this.printFile();
   };

   private postPrint = () => {
      this.isPrinting = false;
      this.logger.debug("Printing done");
      this.wmksService.recoverAllHeartbeatForPrint();
      this.printNextFile();
   };
}
